import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BasicButton } from 'src/components/widgets/buttons';
import { AiPrimaryButton } from 'src/components/widgets/ai';
import { QuestionFreeText } from 'src/components/shared/aiAssistant/questionPrimitives';
import { Icon } from 'src/components/widgets/icon';
import { FileTypeIcon } from 'src/components/widgets/fileTypeIcon';
import Stack from 'src/components/widgets/stack/stack';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { ImageUploadZones } from 'src/components/pages/aiStudio/components/shared/imageUploadZones';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import { T } from 'src/shared/helpers';
import { Notice } from './aiBulkStyles';
import type { ProductImportAttachment } from './productImportClient';

export const WizardArea = styled(Stack)`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 48px 24px 32px;
    background: #fff;
    @media (max-width: 600px) {
        padding: 28px 16px;
    }
`;

export const WizardForm = styled.form`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 100%;
    max-width: 760px;
    box-sizing: border-box;
    margin: auto;
    gap: 32px;
`;

const Intro = styled(Stack)`
    h2 {
        margin: 0;
        color: ${(p) => p.theme.app.primary_dark};
        font-size: clamp(24px, 3vw, 30px);
        font-weight: 600;
        line-height: 1.25;
    }
`;
const Instructions = styled(QuestionFreeText)`
    min-height: 128px;
    padding: 16px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.6;
    &:focus {
        outline: none;
        border-color: ${(p) => p.theme.ai.solid};
    }
`;
const FileCard = styled(Stack)`
    padding: 16px;
    border: 1px solid ${(p) => p.theme.app.borderColor};
    border-radius: 10px;
    min-width: 0;
    @media (max-width: 600px) {
        padding: 12px;
    }
`;
const FileThumbnail = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    overflow: hidden;
    background: #f5f5f9;
    color: #7c8399;
    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;
const FileIdentity = styled(Stack)`
    align-items: center;
    flex: 1;
    min-width: 0;
    span {
        min-width: 0;
        overflow-wrap: anywhere;
        font-size: 13px;
        line-height: 20px;
    }
`;
const FileHeading = styled(Stack)`
    align-items: center;
    justify-content: space-between;
`;
const RemoveFileButton = styled(BasicButton)`
    flex-shrink: 0;
`;
const Description = styled(QuestionFreeText)`
    display: block;
    height: 64px;
    min-height: 64px;
    font-size: 13px;
    line-height: 1.5;
`;
const WizardActions = styled(Stack)`
    align-items: flex-end;
`;
interface Props {
    message: string;
    attachments: ProductImportAttachment[];
    sending: boolean;
    uploading: boolean;
    error: string;
    onMessage: (message: string) => void;
    onAttachments: React.Dispatch<React.SetStateAction<ProductImportAttachment[]>>;
    onUpload: (files: FileList | null) => Promise<ProductImportAttachment[]>;
    onStart: (event: React.FormEvent) => Promise<void>;
}

export function ImportWizard({ message, attachments, sending, uploading, error, onMessage, onAttachments, onUpload, onStart }: Props) {
    const previews = useRef(new Map<string, string>());
    const mounted = useRef(true);
    const [, refreshPreviews] = useState(0);
    const busy = sending || uploading;
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            previews.current.forEach((url) => URL.revokeObjectURL(url));
            previews.current.clear();
        };
    }, []);
    const upload = async (list: FileList | null) => {
        const files = Array.from(list ?? []);
        const uploaded = await onUpload(list);
        if (!mounted.current) return;
        uploaded.forEach((attachment, index) => {
            if (/^image\/(png|jpeg|webp|gif)$/.test(files[index]?.type ?? '')) previews.current.set(attachment.token, URL.createObjectURL(files[index]));
        });
        refreshPreviews((value) => value + 1);
    };
    return (
        <WizardArea>
            <WizardForm
                aria-label={T._('Set up your product import', 'Admin')}
                onSubmit={(event) => {
                    if (busy || !message.trim() || attachments.some((file) => !file.description?.trim())) {
                        event.preventDefault();
                        return;
                    }
                    void onStart(event);
                }}
            >
                <Intro $gap='16px'>
                    <h2>
                        <label htmlFor='ai-bulk-instructions'>{T._('What would you like to import?', 'Admin')}</label>
                    </h2>
                </Intro>
                <Instructions
                    id='ai-bulk-instructions'
                    value={message}
                    onChange={(event) => onMessage(event.target.value)}
                    maxLength={32000}
                    required
                    disabled={busy}
                    placeholder={T._('Describe your products and any import requirements…', 'Admin')}
                />
                <Stack $gap='16px'>
                    <ImageUploadZones onFilesUpload={upload} supportsMultiple disabled={busy || attachments.length >= 10} />
                    {attachments.map((file, index) => (
                        <FileCard key={file.token} $gap='12px'>
                            <FileHeading $horizontal $gap='12px'>
                                <FileIdentity $horizontal $gap='12px'>
                                    <FileThumbnail aria-hidden='true'>
                                        {previews.current.has(file.token) ? (
                                            <img src={previews.current.get(file.token)} alt='' />
                                        ) : (
                                            <FileTypeIcon name={file.name} contentType={file.contentType} size={32} />
                                        )}
                                    </FileThumbnail>
                                    <span>{file.name}</span>
                                </FileIdentity>
                                <ZTooltip title={T._('Remove attachment', 'Admin')}>
                                    <RemoveFileButton
                                        type='button'
                                        $type='icon'
                                        $size='small'
                                        disabled={busy}
                                        aria-label={`${T._('Remove attachment', 'Admin')}: ${file.name}`}
                                        onClick={() => {
                                            const url = previews.current.get(file.token);
                                            if (url) URL.revokeObjectURL(url);
                                            previews.current.delete(file.token);
                                            onAttachments((previous) => previous.filter((item) => item.token !== file.token));
                                        }}
                                    >
                                        <Icon>
                                            <CloseIcon />
                                        </Icon>
                                    </RemoveFileButton>
                                </ZTooltip>
                            </FileHeading>
                            <Description
                                rows={2}
                                id={`ai-bulk-description-${index}`}
                                aria-label={`${T._('File description', 'Admin')}: ${file.name}`}
                                value={file.description ?? ''}
                                maxLength={4000}
                                required
                                disabled={busy}
                                placeholder={T._('What is this file for?', 'Admin')}
                                onChange={(event) => {
                                    const description = event.target.value;
                                    onAttachments((previous) => previous.map((item) => (item.token === file.token ? { ...item, description } : item)));
                                }}
                            />
                        </FileCard>
                    ))}
                </Stack>
                {error && (
                    <Notice $error role='alert'>
                        {error}
                    </Notice>
                )}
                <WizardActions>
                    <AiPrimaryButton type='submit' disabled={busy || !message.trim() || attachments.some((file) => !file.description?.trim())}>
                        {sending ? T._('Starting…', 'Admin') : T._('Start conversation', 'Admin')}
                        <Icon>
                            <ArrowForwardIcon />
                        </Icon>
                    </AiPrimaryButton>
                </WizardActions>
            </WizardForm>
        </WizardArea>
    );
}
